import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  DrawerOverlay, DrawerHeader, DrawerContent, DrawerSection,
  NodeCard, LogTerminal, TaskQueueMini,
  ModalOverlay, ModalContainer, ModalHeader, ModalBody, ModalFooter,
  SkeletonPulse
} from './styles';
import {
  FiSearch, FiTerminal, FiX, FiCopy, FiMail,
  FiPhone, FiTarget, FiActivity, FiZap, FiClock,
  FiCheckCircle, FiAlertTriangle, FiRefreshCw, FiPlus, FiStar,
  FiGlobe, FiDatabase, FiUser
} from 'react-icons/fi';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';

const STATUS_LABELS = {
  new: 'Novo', contacted: 'Contactado', qualified: 'Qualificado',
  converted: 'Convertido', discarded: 'Descartado', enriching: 'Enriquecendo'
};

// Slot-machine animated counter — digits roll like a casino slot
const CELL = 28; // px height per digit cell

function SlotDigit({ digit, delay = 0 }) {
  const isNum = /\d/.test(digit);
  const numVal = isNum ? parseInt(digit) : 0;
  const posRef = useRef(numVal);
  const [pos, setPos] = useState(numVal);
  const stripEl = useRef(null);

  useEffect(() => {
    if (!isNum) return;
    if (numVal !== posRef.current) {
      const diff = numVal - posRef.current;
      const steps = diff > 0 ? diff : diff + 10;
      setPos(p => p + steps);
      posRef.current = numVal;
    }
  }, [numVal, isNum]);

  // Silent reset when accumulated position gets too high (prevents overflow past strip)
  useEffect(() => {
    if (pos >= 80) {
      const timer = setTimeout(() => {
        const el = stripEl.current;
        if (el) {
          el.style.transition = 'none';
          setPos(posRef.current);
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (stripEl.current) stripEl.current.style.transition = `transform 0.5s cubic-bezier(0.15, 0.85, 0.3, 1) ${delay}ms`;
            });
          });
        }
      }, 600 + delay);
      return () => clearTimeout(timer);
    }
  }, [pos >= 80, delay]);

  if (!isNum) {
    return (
      <span style={{ display: 'inline-block', width: 8, height: CELL, lineHeight: `${CELL}px`, textAlign: 'center' }}>
        {digit}
      </span>
    );
  }

  return (
    <span style={{ display: 'inline-block', overflow: 'hidden', height: CELL, width: 16, position: 'relative', verticalAlign: 'top' }}>
      <span ref={stripEl} style={{
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        transition: `transform 0.5s cubic-bezier(0.15, 0.85, 0.3, 1) ${delay}ms`,
        transform: `translateY(-${pos * CELL}px)`
      }}>
        {Array.from({ length: 100 }, (_, i) => (
          <span key={i} style={{ display: 'block', height: CELL, lineHeight: `${CELL}px`, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
            {i % 10}
          </span>
        ))}
      </span>
    </span>
  );
}

function SlotCounter({ value }) {
  const formatted = (value || 0).toLocaleString('pt-BR');
  return (
    <span style={{ display: 'inline-flex', alignItems: 'top', height: CELL, lineHeight: `${CELL}px`, fontVariantNumeric: 'tabular-nums' }}>
      {formatted.split('').map((char, i) => (
        <SlotDigit key={`pos-${i}-len-${formatted.length}`} digit={char} delay={i * 40} />
      ))}
    </span>
  );
}

export default function LeadScraper() {
  const { user } = useAuth();
  const isGod = user?.cargo === 'god';

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [taskStats, setTaskStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [totalLeads, setTotalLeads] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerClosing, setDrawerClosing] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [monitor, setMonitor] = useState(null);
  const [logs, setLogs] = useState([]);
  const [copiedId, setCopiedId] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const wsRef = useRef(null);
  const logTerminalRef = useRef(null);
  const pageRef = useRef(1);
  const hasMoreRef = useRef(true);
  const loadingMoreRef = useRef(false);
  const LIMIT = 40;

  // ═══════ Fetch Stats ═══════
  const fetchStats = useCallback(async () => {
    try {
      const statsRes = await api.get('/lead-scraper/stats');
      if (statsRes.data.success) setStats(statsRes.data.data);
    } catch (e) {
      console.error('Stats fetch error:', e);
    }
    try {
      const taskRes = await api.get('/lead-scraper/tasks/stats');
      if (taskRes.data.success) setTaskStats(taskRes.data.data);
    } catch (e) {
      console.error('Task stats fetch error:', e);
    }
  }, []);

  // ═══════ Fetch Leads ═══════
  const fetchLeads = useCallback(async (pageNum = 1, append = false) => {
    if (append) setLoadingMore(true);
    try {
      const params = { page: pageNum, limit: LIMIT };
      if (searchTerm) params.q = searchTerm;
      if (filterStatus && filterStatus !== 'all') params.status = filterStatus;

      const res = await api.get('/lead-scraper/leads', { params });
      if (res.data.success) {
        const newLeads = res.data.data?.leads || [];
        const total = res.data.data?.total || 0;
        setTotalLeads(total);
        setLeads(prev => append ? [...prev, ...newLeads] : newLeads);
        setHasMore(pageNum * LIMIT < total);
      }
    } catch (e) {
      if (e.response?.status !== 404) console.error('Leads fetch error:', e);
      if (!append) setLeads([]);
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [searchTerm, filterStatus]);

  // ═══════ WebSocket ═══════
  useEffect(() => {
    if (!isGod) return;
    const authData = JSON.parse(localStorage.getItem('vidal_auth') || '{}');
    const token = authData.token;
    if (!token) return;

    const wsUrl = process.env.REACT_APP_WS_URL || 'wss://log.vidal-app.com/ws';
    const ws = new WebSocket(`${wsUrl}?token=${token}`);
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({ action: 'subscribe', type: 'nfe_download_monitor' }));
    };

    ws.onmessage = (event) => {
      try {
        const parsed = JSON.parse(event.data);
        const data = parsed.message || parsed;
        if (data.type === 'nfe_download_monitor' && data.data) {
          setMonitor(data.data);
          if (data.data.logs) {
            setLogs(data.data.logs.slice(-80));
          }
        }
        // Real-time lead update
        if (data.type === 'lead_new' && data.data) {
          setStats(prev => prev ? { ...prev, total: (prev.total || 0) + 1 } : prev);
          setTotalLeads(prev => prev + 1);
          setLeads(prev => [{ id: `rt_${Date.now()}`, ...data.data, status: 'new', capturedAt: new Date().toISOString() }, ...prev]);
        }
        // Full stats sync from backend (accurate totals)
        if (data.type === 'lead_stats_update' && data.data) {
          setStats(data.data);
          setTotalLeads(data.data.total || 0);
        }
      } catch (e) {}
    };

    ws.onerror = () => {};
    ws.onclose = () => {};
    return () => { if (ws.readyState < 2) ws.close(); };
  }, [isGod]);

  // ═══════ Load + Polling ═══════
  useEffect(() => {
    if (!isGod) { setLoading(false); return; }
    fetchStats();
    fetchLeads(1, false);
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, [isGod, fetchStats, fetchLeads]);

  // Reset on filter/search change
  useEffect(() => {
    if (!isGod) return;
    setPage(1);
    setHasMore(true);
    fetchLeads(1, false);
  }, [searchTerm, filterStatus, fetchLeads, isGod]);

  // Keep refs in sync
  useEffect(() => { pageRef.current = page; }, [page]);
  useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);
  useEffect(() => { loadingMoreRef.current = loadingMore; }, [loadingMore]);

  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 400 && hasMoreRef.current && !loadingMoreRef.current) {
      const nextPage = pageRef.current + 1;
      setPage(nextPage);
      fetchLeads(nextPage, true);
    }
  }, [fetchLeads]);

  useEffect(() => {
    if (logTerminalRef.current) {
      logTerminalRef.current.scrollTop = logTerminalRef.current.scrollHeight;
    }
  }, [logs]);

  // ═══════ Actions ═══════
  const handleCopyEmail = (e, email, id) => {
    e.stopPropagation();
    navigator.clipboard.writeText(email);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
    toast.success('Email copiado!', { autoClose: 1500 });
  };

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await api.patch(`/lead-scraper/leads/${leadId}/status`, { status: newStatus });
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, status: newStatus } : l));
      if (selectedLead?.id === leadId) setSelectedLead(s => ({ ...s, status: newStatus }));
      toast.success(`Status → ${STATUS_LABELS[newStatus] || newStatus}`);
    } catch (e) {
      toast.error('Erro ao atualizar status');
    }
  };

  const handleSeedTasks = async () => {
    if (seeding) return;
    setSeeding(true);
    try {
      const res = await api.post('/lead-scraper/tasks/seed');
      if (res.data.success) {
        toast.success(`${res.data.data?.created || 0} tasks criadas!`);
        fetchStats();
      }
    } catch (e) {
      toast.error('Erro ao criar tasks');
    } finally {
      setSeeding(false);
    }
  };

  const handleCloseDrawer = () => {
    setDrawerClosing(true);
    setTimeout(() => { setDrawerOpen(false); setDrawerClosing(false); }, 200);
  };

  const handleRefresh = () => {
    fetchStats();
    fetchLeads();
    toast.info('Atualizado!', { autoClose: 1000 });
  };

  // ═══════ Computed ═══════
  const activeNodes = monitor?.nodes?.filter(n => n.status !== 'offline') || [];
  const scrapingNodes = activeNodes.filter(n =>
    n.status === 'scraping' || n.currentKey?.includes('[Maps]') || n.currentKey?.includes('[Enrich]')
  );
  const isLive = activeNodes.length > 0;

  if (!isGod) {
    return (
      <Container>
        <div className="content-wrapper">
          <div className="empty-state">
            <FiAlertTriangle />
            <p>Acesso restrito</p>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <div className="content-wrapper">
        {/* ═══════ HEADER ═══════ */}
        <div className="header-info">
          <div className="header-icon"><FiTarget /></div>
          <div className="header-details">
            <h3>Lead Scraper</h3>
            <div className="header-meta">
              {isLive ? (
                <span className="status-tag active"><span className="dot" />{scrapingNodes.length} nós scraping</span>
              ) : (
                <span className="status-tag offline">Offline</span>
              )}
              <span className="update-tag">Puppeteer Cluster</span>
            </div>
          </div>
          <div className="toolbar-right" style={{ display: 'flex', gap: 8 }}>
            <button className="btn-action" onClick={handleRefresh}>
              <FiRefreshCw /> Atualizar
            </button>
            <button className={`btn-action ${drawerOpen ? 'primary' : ''}`} onClick={() => drawerOpen ? handleCloseDrawer() : setDrawerOpen(true)}>
              <FiTerminal /> Monitor
            </button>
          </div>
        </div>

        {/* ═══════ KPIs ═══════ */}
        <div className="header-stats">
          <div className="stat-card purple">
            <div className="stat-icon"><FiDatabase /></div>
            <div>
              <div className="stat-value"><SlotCounter value={stats?.total || 0} /></div>
              <div className="stat-label">Total Leads</div>
            </div>
          </div>
          <div className="stat-card success">
            <div className="stat-icon"><FiMail /></div>
            <div>
              <div className="stat-value"><SlotCounter value={stats?.withEmail || 0} /></div>
              <div className="stat-label">Com Email</div>
            </div>
          </div>
          <div className="stat-card warning">
            <div className="stat-icon"><FiPhone /></div>
            <div>
              <div className="stat-value"><SlotCounter value={stats?.withPhone || 0} /></div>
              <div className="stat-label">Com Telefone</div>
            </div>
          </div>
          <div className="stat-card cyan">
            <div className="stat-icon"><FiZap /></div>
            <div>
              <div className="stat-value"><SlotCounter value={stats?.last24h || 0} /></div>
              <div className="stat-label">Últimas 24h</div>
            </div>
          </div>
          <div className="stat-card pink">
            <div className="stat-icon"><FiCheckCircle /></div>
            <div>
              <div className="stat-value">{taskStats?.completed || 0}/{taskStats?.total || 0}</div>
              <div className="stat-label">Tasks</div>
            </div>
          </div>
        </div>

        {/* ═══════ TOOLBAR ═══════ */}
        <div className="toolbar">
          <div className="toolbar-left">
            <button className={`filter-btn ${filterStatus === 'all' ? 'active' : ''}`} onClick={() => { setFilterStatus('all'); setPage(1); }}>Todos</button>
            <button className={`filter-btn ${filterStatus === 'new' ? 'active' : ''}`} onClick={() => { setFilterStatus('new'); setPage(1); }}>Novos</button>
            <button className={`filter-btn ${filterStatus === 'contacted' ? 'active' : ''}`} onClick={() => { setFilterStatus('contacted'); setPage(1); }}>Contactados</button>
            <button className={`filter-btn ${filterStatus === 'qualified' ? 'active' : ''}`} onClick={() => { setFilterStatus('qualified'); setPage(1); }}>Qualificados</button>
            <div className="search-input">
              <FiSearch />
              <input
                type="text"
                placeholder="Buscar leads..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
              />
            </div>
          </div>
          <div className="toolbar-right">
            <button className="btn-action" onClick={handleSeedTasks} disabled={seeding}>
              <FiPlus />
              {seeding ? 'Criando...' : 'Seed Tasks'}
            </button>
          </div>
        </div>

        {/* ═══════ LEADS GRID ═══════ */}
        <div className="leads-list-container" onScroll={handleScroll}>
          {loading ? (
            <div className="leads-grid">
              {[...Array(8)].map((_, i) => <SkeletonPulse key={i} $height="220px" $radius="12px" />)}
            </div>
          ) : leads.length === 0 ? (
            <div className="empty-state">
              <FiUser />
              <p>Nenhum lead encontrado</p>
              <small>Execute "Seed Tasks" para criar tarefas de scraping</small>
            </div>
          ) : (
            <>
              <div className="leads-count">{totalLeads.toLocaleString()} leads</div>
              <div className="leads-grid">
                {leads.map(lead => (
                  <div
                    key={lead.id}
                    className={`lead-card ${lead.email ? 'has-email' : ''}`}
                    onClick={() => setSelectedLead(lead)}
                  >
                    <div className="card-top">
                      <div className="card-thumb">
                        {(lead.nome || '?')[0].toUpperCase()}
                      </div>
                      <div className="card-top-right">
                        <div className="card-name">{lead.nome}</div>
                        {lead.categoria && (
                          <div className="card-categoria">{lead.categoria}</div>
                        )}
                      </div>
                      {lead.rating && (
                        <div className="card-rating">
                          <FiStar />{lead.rating}
                        </div>
                      )}
                    </div>

                    <div className="card-info">
                      {lead.telefone && (
                        <div className="info-row">
                          <FiPhone /><span>{lead.telefone}</span>
                        </div>
                      )}
                      {lead.email && (
                        <div className="info-row email">
                          <FiMail /><span>{lead.email}</span>
                        </div>
                      )}
                      {(lead.cidade || lead.uf) && (
                        <div className="info-row">
                          <FiTarget /><span>{lead.cidade}{lead.uf ? `/${lead.uf}` : ''}</span>
                        </div>
                      )}
                    </div>

                    <div className="card-footer">
                      <span className={`tag status-${lead.status || 'new'}`}>
                        {STATUS_LABELS[lead.status] || 'Novo'}
                      </span>
                      {lead.segmento && <span className="tag segmento">{lead.segmento}</span>}
                    </div>

                    {lead.email && (
                      <button
                        className={`card-copy ${copiedId === lead.id ? 'copied' : ''}`}
                        onClick={(e) => handleCopyEmail(e, lead.email, lead.id)}
                        title="Copiar email"
                      >
                        <FiCopy />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {loadingMore && (
                <div className="loading-more">
                  <FiRefreshCw className="spin" /> Carregando mais...
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ═══════ DRAWER — MONITOR COMPLETO ═══════ */}
      {drawerOpen && (
        <DrawerOverlay className={drawerClosing ? 'closing' : ''}>
          <DrawerHeader>
            <h3>Monitor Central</h3>
            <button className="close-btn" onClick={handleCloseDrawer}><FiX /></button>
          </DrawerHeader>
          <DrawerContent>
            {/* ── Velocidade Global ── */}
            <DrawerSection>
              <div className="section-title">Velocidade Global</div>
              <TaskQueueMini>
                <div className="task-stat">
                  <div className="task-stat-value">{monitor?.rates?.perMinute || 0}</div>
                  <div className="task-stat-label">por minuto</div>
                </div>
                <div className="task-stat">
                  <div className="task-stat-value">{monitor?.rates?.perHour || 0}</div>
                  <div className="task-stat-label">por hora</div>
                </div>
                <div className="task-stat">
                  <div className="task-stat-value">{monitor?.rates?.totalSession || 0}</div>
                  <div className="task-stat-label">sessão</div>
                </div>
              </TaskQueueMini>
              <TaskQueueMini style={{ marginTop: 8 }}>
                <div className="task-stat">
                  <div className="task-stat-value">{monitor?.rates?.avgDownloadTime ? `${(monitor.rates.avgDownloadTime / 1000).toFixed(1)}s` : '—'}</div>
                  <div className="task-stat-label">tempo médio</div>
                </div>
                <div className="task-stat">
                  <div className="task-stat-value">{monitor?.rates?.totalErrors || 0}</div>
                  <div className="task-stat-label">erros</div>
                </div>
                <div className="task-stat">
                  <div className="task-stat-value">{monitor?.rates?.uptime ? `${Math.round(monitor.rates.uptime / 60)}m` : '—'}</div>
                  <div className="task-stat-label">uptime</div>
                </div>
              </TaskQueueMini>
            </DrawerSection>

            {/* ── Fila de Tasks ── */}
            <DrawerSection>
              <div className="section-title">Fila de Tasks</div>
              <TaskQueueMini>
                <div className="task-stat">
                  <div className="task-stat-value">{taskStats?.pending || 0}</div>
                  <div className="task-stat-label">Pendentes</div>
                </div>
                <div className="task-stat">
                  <div className="task-stat-value">{taskStats?.assigned || 0}</div>
                  <div className="task-stat-label">Em Progresso</div>
                </div>
                <div className="task-stat">
                  <div className="task-stat-value">{taskStats?.completed || 0}</div>
                  <div className="task-stat-label">Completas</div>
                </div>
              </TaskQueueMini>
            </DrawerSection>

            {/* ── Nós Conectados ── */}
            <DrawerSection>
              <div className="section-title">Nós Conectados ({monitor?.nodeCount || 0}) — {monitor?.activeNodes || 0} ativos</div>
              {(monitor?.nodes || []).length === 0 ? (
                <div style={{ fontSize: 12, opacity: 0.5, padding: '8px 0' }}>Nenhum nó conectado</div>
              ) : (
                (monitor?.nodes || []).map(node => (
                  <NodeCard key={node.nodeId}>
                    <div className="node-header">
                      <span className="node-name">
                        {node.nodeId?.replace('node-', '').split('-').slice(0, 2).join('-')}
                        {node.isLocalNetwork && ' (local)'}
                      </span>
                      <span className={`node-status ${node.status}`}>{node.status}</span>
                    </div>
                    {node.currentKey && (
                      <div className="node-task">{node.currentKey}</div>
                    )}
                    <div className="node-stats">
                      <span><FiActivity /> {node.sessionDownloads || 0} XMLs</span>
                      <span><FiZap /> {node.rates?.perHour || 0}/h</span>
                      <span><FiClock /> {node.uptimeSeconds ? `${Math.round(node.uptimeSeconds / 60)}min` : '—'}</span>
                    </div>
                    <div className="node-stats">
                      <span>Avg: {node.avgDownloadTime ? `${(node.avgDownloadTime / 1000).toFixed(1)}s` : '—'}</span>
                      <span>Erros: {node.sessionErrors || 0}</span>
                      <span>CAPTCHA: {node.sessionCaptchaErrors || 0}</span>
                    </div>
                  </NodeCard>
                ))
              )}
            </DrawerSection>

            {/* ── Logs ── */}
            <DrawerSection>
              <div className="section-title">Logs ({logs.length})</div>
              <LogTerminal ref={logTerminalRef}>
                {logs.length === 0 ? (
                  <div className="log-entry"><span className="log-msg info">Aguardando atividade...</span></div>
                ) : (
                  logs.map((log, i) => (
                    <div className="log-entry" key={i}>
                      <span className="log-time">
                        {new Date(log.timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                      </span>
                      <span className={`log-msg ${log.level || 'info'}`}>{log.message}</span>
                    </div>
                  ))
                )}
              </LogTerminal>
            </DrawerSection>
          </DrawerContent>
        </DrawerOverlay>
      )}

      {/* ═══════ DETAIL MODAL ═══════ */}
      {selectedLead && (
        <ModalOverlay onClick={() => setSelectedLead(null)}>
          <ModalContainer onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <div className="modal-title">
                <FiTarget />
                <h2>{selectedLead.nome}</h2>
              </div>
              <button className="modal-close" onClick={() => setSelectedLead(null)}><FiX /></button>
            </ModalHeader>
            <ModalBody>
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-label">Email</span>
                  <span className="detail-value" style={selectedLead.email ? { color: '#10b981' } : {}}>
                    {selectedLead.email || '—'}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Telefone</span>
                  <span className="detail-value">{selectedLead.telefone || '—'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">WhatsApp</span>
                  <span className="detail-value">{selectedLead.whatsapp || '—'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">CNPJ</span>
                  <span className="detail-value">{selectedLead.cnpj || '—'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Cidade / UF</span>
                  <span className="detail-value">{selectedLead.cidade || '—'} / {selectedLead.uf || '—'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Segmento</span>
                  <span className="detail-value">{selectedLead.segmento || '—'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Rating</span>
                  <span className="detail-value">
                    {selectedLead.rating ? `${selectedLead.rating} ★ (${selectedLead.reviews || 0} reviews)` : '—'}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Status</span>
                  <span className="detail-value">{STATUS_LABELS[selectedLead.status] || 'Novo'}</span>
                </div>
                {selectedLead.site && (
                  <div className="detail-item full-width">
                    <span className="detail-label">Website</span>
                    <span className="detail-value">
                      <a href={selectedLead.site} target="_blank" rel="noreferrer">{selectedLead.site}</a>
                    </span>
                  </div>
                )}
                {selectedLead.mapsUrl && (
                  <div className="detail-item full-width">
                    <span className="detail-label">Google Maps</span>
                    <span className="detail-value">
                      <a href={selectedLead.mapsUrl} target="_blank" rel="noreferrer">Abrir no Maps</a>
                    </span>
                  </div>
                )}
                {selectedLead.emailsAll?.length > 1 && (
                  <div className="detail-item full-width">
                    <span className="detail-label">Todos Emails</span>
                    <span className="detail-value">{selectedLead.emailsAll.join(', ')}</span>
                  </div>
                )}
                {selectedLead.socialMedia && Object.keys(selectedLead.socialMedia).length > 0 && (
                  <div className="detail-item full-width">
                    <span className="detail-label">Redes Sociais</span>
                    <span className="detail-value">
                      {Object.entries(selectedLead.socialMedia).map(([k, v]) => (
                        <span key={k} style={{ marginRight: 12 }}>
                          <a href={v} target="_blank" rel="noreferrer">{k}</a>
                        </span>
                      ))}
                    </span>
                  </div>
                )}
                <div className="detail-item">
                  <span className="detail-label">Capturado em</span>
                  <span className="detail-value">
                    {selectedLead.capturedAt ? new Date(selectedLead.capturedAt).toLocaleString('pt-BR') : '—'}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Enriquecido</span>
                  <span className="detail-value">
                    {selectedLead.enriched
                      ? `✓ ${selectedLead.enrichedAt ? new Date(selectedLead.enrichedAt).toLocaleString('pt-BR') : ''}`
                      : '✗ Não'
                    }
                  </span>
                </div>
              </div>
            </ModalBody>
            <ModalFooter>
              <button className="modal-btn" onClick={() => handleStatusChange(selectedLead.id, 'contacted')}>
                Contactado
              </button>
              <button className="modal-btn" onClick={() => handleStatusChange(selectedLead.id, 'qualified')}>
                Qualificar
              </button>
              <button className="modal-btn" onClick={() => handleStatusChange(selectedLead.id, 'discarded')}>
                Descartar
              </button>
              {selectedLead.email && (
                <button className="modal-btn primary" onClick={(e) => handleCopyEmail(e, selectedLead.email, selectedLead.id)}>
                  Copiar Email
                </button>
              )}
            </ModalFooter>
          </ModalContainer>
        </ModalOverlay>
      )}
    </Container>
  );
}
